import { AgGridReact, Grid } from "@jsq/ds/core";
import { useGetAnnouncementsQuery } from "~/graphql/supergraphHooks";
import { AnnouncementStatus } from "./AnnouncementStatus";
import ArenaGroupsDisplay from "./AudienceDisplay";
import DeleteCellRenderer from "./DeleteCellRenderer";
import { AnnouncementListItem, TypedAGGridCellRenderParams } from "./types";
import { formatDisplayDate, mapAnnouncements } from "./utils";

/**
 * @todo add delete operation for each row
 * @todo initial and ongoing filter of arena only announcements
 * @todo line beside announcment name
 * @todo [stretch goal] add detail view for each announcement (no edit)
 * @todo [stretch goal] add edit behaviour for announcements when clicked
 * @todo [stretch goal] add bulk delete operation
 * @todo [stretch goal] number of users viewed the announcement column
 * @todo [stretch goal] ensuring date sorting is correct
 */
export const AnnouncementList = () => {
  const { data } = useGetAnnouncementsQuery();
  const announcements = mapAnnouncements(data);

  /**
   * Defines the grid options used for the announcement list.
   *
   * @see https://www.ag-grid.com/javascript-data-grid/grid-options
   */
  const gridOptions: AgGridReact.AgGridReactProps<AnnouncementListItem>["gridOptions"] = {
    autoSizeStrategy: {
      /**
       * The columns will scale to fit the available grid width (or a provided width if desired)
       */
      type: "fitGridWidth",
    },
    /**
     * The grid's height is set to fit the number of rows so no vertical scrollbar is provided by the grid.
     * The grid scrolls horizontally as normal.
     *
     * @note that if using this with the SSRM the grid will attempt to load every row and may cause undesired side-effects
     *   (such as excessive datasource requests or too many loaded rows).
     * @todo review interaction with SSRM (see note above) after ENG-25573
     * @see https://www.ag-grid.com/javascript-data-grid/grid-size/#auto-height-layout
     */
    domLayout: "autoHeight",
    /**
     * @todo after pagination is added on BE in ENG-25573, update this to serverSide
     */
    rowModelType: "clientSide",
    /**
     * A pure function that returns a string ID to uniquely identify a given row. This enables the grid to
     * work optimally with data changes and updates.
     */
    getRowId: (params) => params.data.id.toString(),
  };

  /**
   * Column definitions for the AG Grid used in the AnnouncementList component.
   *
   * @type {AgGridReact.AgGridReactProps<AnnouncementListItem>["columnDefs"]}
   * @property {Object} announcementName - Column definition for the announcement name.
   * @property {Object} creationDate - Column definition for the creation date with a custom value formatter.
   * @property {Object} expirationDate - Column definition for the expiration date with a custom value formatter.
   * @property {Object} status - Column definition for the status with a custom cell renderer.
   * @property {Object} creator - Column definition for the creator.
   */
  const colDefs: AgGridReact.AgGridReactProps<AnnouncementListItem>["columnDefs"] = [
    {
      field: "name",
      headerName: "Announcement name",
    },
    {
      field: "header",
      /**
       * Extra context fields, hidden by default
       */
      hide: true,
    },
    {
      field: "body",
      /**
       * Extra context fields, hidden by default
       */
      hide: true,
    },
    {
      field: "createdAt",
      headerName: "Created date",
      valueFormatter: (params) =>
        formatDisplayDate(
          params?.data?.createdAt ? new Date(params.data.createdAt) : undefined,
        ),
    },
    {
      field: "expiredAt",
      headerName: "Expiry date",
      valueFormatter: (params) => formatDisplayDate(params?.data?.expiredAt),
    },
    {
      field: "status",
      cellRenderer: (params: TypedAGGridCellRenderParams<AnnouncementListItem>) => (
        <AnnouncementStatus status={params.data.status} />
      ),
    },
    {
      field: "arenaGroups",
      headerName: "Arena audience",
      cellRenderer: (params: TypedAGGridCellRenderParams<AnnouncementListItem>) => (
        <ArenaGroupsDisplay arenaGroups={params.data.arenaGroups} />
      ),
      cellStyle: { display: "flex", alignItems: "center" },
    },
    { field: "roleCount", headerName: "User roles" },
    {
      field: "createdBy",
      headerName: "Created by",
    },
    {
      field: "url",
      /**
       * Extra context fields, hidden by default
       */
      hide: true,
    },
    {
      field: "delete",
      /**
       * Extra context fields, hidden by default
       */
      maxWidth: 100,
      resizable: false,
      suppressMenu: true,
      suppressMovable: true,
      sortable: false,
      headerName: "",
      cellRenderer: (params: TypedAGGridCellRenderParams<AnnouncementListItem>) => (
        <DeleteCellRenderer
          notificationId={params.data.delete.notificationId}
          notificationStatus={params.data.delete.status}
        />
      ),
      cellStyle: { display: "flex", alignItems: "center" },
    },
  ];

  return (
    <Grid gridOptions={gridOptions} columnDefs={colDefs} rowData={announcements} pagination />
  );
};
